import {
  Divider,
  Grid,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Box,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import { Autocomplete } from '@material-ui/lab';
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';
import React, { useEffect, useState, useRef } from 'react';
import useApi from '../../../utils/useApi';
import { formattedCurrency, normalizeText } from '../../../utils/utils';
import ClientCreateModal from '../../Client/ClientCreateModal';

const SaleSelect = (children) => {
  const {
    options,
    listItens,
    selectClient,
    selectPromotion,
    setDiscount,
    selectPlots,
    selectInterest,
    changeSearchItens,
    inputBarCode,
    confirmSale,
    qntd,
    setQntd,
  } = children;

  const inputCustumer = document.getElementById('sale-new-select-customer');
  const inputPromotion = document.getElementById('sale-new-select-promotion');
  const inputInterest = document.getElementById('sale-new-select-interest');

  const [openClientModal, setOpenClientModal] = useState(false);
  const [clientInitialName, setClientInitialName] = useState('');
  const lastKeyTimeRef = useRef(0);

  const [promotionsLoad, promotionsInfo] = useApi({
    method: 'get',
    url: '/promotions',
    params: { isActive: 1, customerProfile: [1], isExpired: 0 },
  });

  const [clientLoad, clientInfo] = useApi({
    method: 'get',
    url: '/customers',
  });

  const [analyzeLoad, analyzeInfo] = useApi({
    method: 'get',
    url: '/customers/1/analysis',
  });

  useEffect(() => {
    return () => {
      document.onkeyup = null;
    };
  }, []);

  useEffect(() => {
    const customerId = options.customer?.id || (typeof options.customer === 'number' ? options.customer : null);
    if (!customerId) return;

    if (customerId != 1) {
      analyzeLoad({ url: `/customers/${customerId}/analysis` });
    } else {
      promotionsLoad({ params: { isActive: 1, customerProfile: [1], isExpired: 0 } });
    }
  }, [options.customer?.id || options.customer]);

  useEffect(() => {
    if (analyzeInfo.data && analyzeInfo.data.profiles) {
      const profileIds = analyzeInfo.data.profiles.map((p) => p.id);
      promotionsLoad({ params: { isActive: 1, customerProfile: profileIds, isExpired: 0 } });
    }
  }, [analyzeInfo.data]);

  const parseDateHelper = (dateStr) => {
    if (!dateStr) return null;
    try {
      const str = String(dateStr).trim();
      if (/^\d{2}[-/]\d{2}[-/]\d{4}/.test(str)) {
        const [datePart, timePart] = str.split(' ');
        const [day, month, year] = datePart.split(/[-/]/);
        return new Date(`${year}-${month}-${day}T${timePart || '00:00:00'}`);
      }
      return new Date(str);
    } catch (e) {
      return null;
    }
  };

  const isPromoEligibleForCart = (promo) => {
    // 1. Promoção padrão é sempre elegível
    if (!promo || promo.id === 1) return true;

    // 2. Status e vigência
    if (promo.isActive === 0 || promo.isActive === false || promo.isActive === '0') return false;
    if (promo.isExpired || promo.isLimitReached) return false;
    const now = new Date();
    if (promo.startDate) {
      const sDate = parseDateHelper(promo.startDate);
      if (sDate && !isNaN(sDate.getTime()) && sDate > now) return false;
    }
    if (promo.endDate) {
      const eDate = parseDateHelper(promo.endDate);
      if (eDate && !isNaN(eDate.getTime()) && eDate < now) return false;
    }

    // 3. Itens do carrinho
    const cartProducts = Array.isArray(options.products) && options.products.length > 0
      ? options.products
      : (listItens ? listItens.filter((i) => i.typeItem === 'product') : []);
    const cartServices = Array.isArray(options.services) && options.services.length > 0
      ? options.services
      : (listItens ? listItens.filter((i) => i.typeItem === 'service') : []);

    const allItems = [...cartProducts, ...cartServices];
    if (allItems.length === 0) {
      return false;
    }

    const currentTotalSum = Number(options.totalSum) || allItems.reduce(
      (acc, it) => acc + ((Number(it.price) || Number(it.valueUn) || 0) * (Number(it.amount) || 0)),
      0
    );
    const currentTotalQty = allItems.reduce((acc, it) => acc + (Number(it.amount) || 0), 0);

    // 4. Valor mínimo da compra
    if (promo.minValue && Number(promo.minValue) > 0) {
      if (currentTotalSum < Number(promo.minValue)) return false;
    }

    // 5. Quantidade mínima de itens
    if (promo.minQuantity && Number(promo.minQuantity) > 0) {
      if (currentTotalQty < Number(promo.minQuantity)) return false;
    }

    // 6. Regras de elegibilidade por item
    let promoCats = [];
    if (Array.isArray(promo.categories)) promoCats = promo.categories.map(String);
    else if (typeof promo.categories === 'string') {
      try { promoCats = JSON.parse(promo.categories).map(String); } catch (e) { promoCats = []; }
    }

    let promoProds = [];
    if (Array.isArray(promo.products)) promoProds = promo.products.map(String);
    else if (typeof promo.products === 'string') {
      try { promoProds = JSON.parse(promo.products).map(String); } catch (e) { promoProds = []; }
    }

    let promoServs = [];
    if (Array.isArray(promo.services)) promoServs = promo.services.map(String);
    else if (typeof promo.services === 'string') {
      try { promoServs = JSON.parse(promo.services).map(String); } catch (e) { promoServs = []; }
    }

    // Restrição por categoria
    if (promoCats.length > 0) {
      const hasMatchingCat = allItems.some((item) => {
        const itemCat = item.category ? String(typeof item.category === 'object' ? item.category.id : item.category) : '';
        return itemCat && promoCats.includes(itemCat);
      });
      if (!hasMatchingCat) return false;
    }

    // Restrição por produto ou serviço específico
    const hasItemRestrictions = promoProds.length > 0 || promoServs.length > 0;
    if (hasItemRestrictions) {
      const hasMatchingProd = cartProducts.some((p) => promoProds.includes(String(p.id)));
      const hasMatchingServ = cartServices.some((s) => promoServs.includes(String(s.id)));
      if (!hasMatchingProd && !hasMatchingServ) return false;
    }

    return true;
  };

  useEffect(() => {
    if (options.promotion && options.promotion.id !== 1 && promotionsInfo.data) {
      const currentPromo = promotionsInfo.data.find((p) => String(p.id) === String(options.promotion.id)) || options.promotion;
      if (!isPromoEligibleForCart(currentPromo)) {
        const defaultPromo = promotionsInfo.data.find((p) => p.id === 1) || { id: 1, name: 'Sem promoção' };
        selectPromotion(defaultPromo);
      }
    }
  }, [options.products, options.services, options.totalSum, listItens]);

  document.onkeyup = (event) => {
    const { code, shiftKey } = event;
    if (shiftKey && code === 'KeyA') {
      const el = inputBarCode || document.getElementById('sale-new-search-item');
      if (el) el.focus();
    }
    if (shiftKey && code === 'KeyD') {
      inputPromotion.focus();
    }
    if (code === 'ArrowUp' || code === 'ArrowDown') {
      const now = Date.now();
      if (now - lastKeyTimeRef.current < 200) return;
      lastKeyTimeRef.current = now;
      if (code === 'ArrowUp') setQntd(qntd + 1);
      if (code === 'ArrowDown') {
        if (Number(qntd) > 1) setQntd(qntd - 1);
      }
    }
    if (shiftKey && code === 'ArrowRight') {
      // verifica se está focado para nao executar onfocus denovo
      if (inputInterest !== document.activeElement) {
        inputInterest.focus();
      }
      selectInterest(options.interest - 1);
    }
    if (shiftKey && code === 'ArrowLeft') {
      if (inputInterest !== document.activeElement) {
        inputInterest.focus();
      }
      selectInterest(options.interest + 1);
    }
    if (shiftKey && code === 'KeyC') {
      inputCustumer.focus();
    }
    if (shiftKey && code === 'Space') {
      changeSearchItens();
    }
    if (shiftKey && code === 'Enter') {
      confirmSale();
    }
  };

  return (
    <>
      <Grid item xs={12}>
        <Autocomplete
          disableClearable
          id="sale-new-select-customer"
          noOptionsText={clientInfo.loading ? 'Carregando clientes...' : 'Sem opções'}
          loading={clientInfo.loading}
          onOpen={() => {
            if (!clientInfo.data && !clientInfo.loading) {
              clientLoad();
            }
          }}
          disabled={clientInfo.loading}
          value={options.customer}
          options={clientInfo.data !== null ? clientInfo.data : (options.customer ? [options.customer] : [])}
          getOptionSelected={(option, value) => option.id === value?.id}
          getOptionLabel={(client) =>
            client ? `${client.name || ''} ${client.lastName || client.last_name || ''}`.trim() : ''
          }
          filterOptions={(opts, state) => {
            const term = normalizeText(state.inputValue);
            const filtered = !term
              ? [...opts]
              : opts.filter((option) => {
                  const fullName = normalizeText(`${option.name || ''} ${option.lastName || option.last_name || ''}`);
                  const cpf = normalizeText(option.cpf);
                  const phone = normalizeText(option.phone || option.whatsapp);
                  const id = String(option.id);
                  return fullName.includes(term) || cpf.includes(term) || phone.includes(term) || id.includes(term);
                });

            if (state.inputValue && state.inputValue.trim()) {
              const exactMatch = filtered.some(
                (opt) => normalizeText(`${opt.name || ''} ${opt.lastName || ''}`).trim() === term
              );
              if (!exactMatch) {
                filtered.push({
                  id: '__create_new_client__',
                  isCreateNew: true,
                  name: state.inputValue.trim(),
                });
              }
            }
            return filtered;
          }}
          renderOption={(option) => {
            if (option.isCreateNew) {
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', color: '#17497F', fontWeight: 600 }}>
                  <PersonAddIcon style={{ fontSize: 18 }} />
                  <span>+ Cadastrar novo cliente: "{option.name}"</span>
                </div>
              );
            }
            const fullName = `${option.name || ''} ${option.lastName || option.last_name || ''}`.trim();
            return (
              <div style={{ display: 'flex', flexDirection: 'column', padding: '2px 0' }}>
                <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>
                  {fullName || option.name}
                </span>
                {(option.cpf || option.phone || option.whatsapp) && (
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    {[option.cpf ? `CPF: ${option.cpf}` : null, option.phone || option.whatsapp].filter(Boolean).join(' • ')}
                  </span>
                )}
              </div>
            );
          }}
          onChange={(ev, value) => {
            if (value && value.isCreateNew) {
              setClientInitialName(value.name);
              setOpenClientModal(true);
              return;
            }
            selectClient(value);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Selecionar cliente (Shift + C)"
              variant="standard"
              placeholder="Nome, sobrenome, CPF..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    <Tooltip title="Cadastrar Novo Cliente">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setClientInitialName(params.inputProps.value || '');
                          setOpenClientModal(true);
                        }}
                        style={{ padding: 4, color: '#17497F' }}
                      >
                        <PersonAddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {options.customer && options.customer.id !== 1 && analyzeInfo.data && analyzeInfo.data.profiles && (
          <Box display="flex" flexWrap="wrap" gridGap={4} marginTop={1}>
            {analyzeInfo.data.profiles.map((profile) => (
              <Chip
                key={profile.id}
                label={profile.name}
                size="small"
                style={{
                  backgroundColor: Number(profile.type) === 2 ? '#D60429' : '#2196f3',
                  color: 'white',
                  fontWeight: 'bold',
                }}
              />
            ))}
          </Box>
        )}
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={12} md={6}>
          <Autocomplete
            disableClearable
            id="sale-new-select-promotion"
            noOptionsText="Nenhuma promoção elegível"
            value={options.promotion}
            getOptionSelected={(option, value) => option.id === value.id}
            disabled={promotionsInfo.loading}
            options={promotionsInfo.data !== null ? promotionsInfo.data.filter(isPromoEligibleForCart) : []}
            onChange={(ev, value) => selectPromotion(value)}
            getOptionLabel={(promotion) => promotion.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Aplicar promoção"
                variant="standard"
                placeholder="Aplicar uma promoção a venda"
                helperText="(Shift + D)"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CurrencyTextField
            type="tel"
            variant="standard"
            decimalCharacter=","
            digitGroupSeparator="."
            currencySymbol="R$"
            outputFormat="number"
            label="Dar desconto"
            value={options.discount}
            onBlur={(ev) => setDiscount(ev.target.value)}
            fullWidth
            disabled={options.promotion && options.promotion.id != 1}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={12} md={6}>
          <TextField
            id="sale-new-select-plots"
            label="Parcelamento"
            defaultValue={options.plots}
            onBlur={(ev) => {
              if (!ev.target.value || ev.target.value === '0') {
                selectPlots(1);
              }
              else {
                selectPlots(ev.target.value);
              }
            }}
            placeholder="Qnts parcelas"
            type="number"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Aplicar juros %"
            id="sale-new-select-interest"
            defaultValue={options.interest}
            onBlur={(ev) => {
              if (!ev.target.value || ev.target.value === '0') {
                selectInterest(0);
              } else {
                selectInterest(ev.target.value);
              }
            }}
            placeholder="Informe a quantidade de juros "
            type="number"
            helperText="Shift + Setas ⬅ ➡"
            fullWidth
          />
        </Grid>
      </Grid>

      <Divider style={{ margin: '5px 0' }} />
      <TableContainer>
        <Typography align="center" variant="subtitle2" component="div">
          Info do pedido
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cliente</TableCell>
              <TableCell>
                {options.customer
                  ? `${options.customer.name} ${
                      options.customer.lastName || ''
                    }`
                  : 'Avulso'}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Pagamento</TableCell>
              <TableCell>{`Em ${
                options.plots > 0 ? options.plots : 1
              }x`} {options.total ? `De ${formattedCurrency(options.total / (options.plots > 0 ? options.plots : 1))}` : ''}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Acréscimo</TableCell>
              <TableCell>{formattedCurrency(options.increase || 0)}</TableCell>
            </TableRow>
          </TableHead>
        </Table>
      </TableContainer>

      <ClientCreateModal
        open={openClientModal}
        onClose={() => setOpenClientModal(false)}
        initialName={clientInitialName}
        onSuccess={(newCustomer) => {
          if (clientInfo.data && Array.isArray(clientInfo.data)) {
            clientInfo.data.unshift(newCustomer);
          }
          selectClient(newCustomer);
          setOpenClientModal(false);
        }}
      />
    </>
  );
};

export default SaleSelect;
